import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, Chip } from '@heroui/react';
import { cn } from '../../../utils/cn.js';
import Grainient from '../../ui/Grainient.jsx';

const themeStyles = {
    blue: {
        active: "text-white border-blue-500/50 shadow-lg shadow-blue-500/20",
        inactive: "bg-blue-50/50 text-slate-600 border-blue-200 hover:bg-blue-100 hover:border-blue-300 hover:text-blue-700 dark:bg-blue-900/10 dark:text-slate-400 dark:border-blue-800/50 dark:hover:bg-blue-900/20 dark:hover:text-blue-400",
        iconActive: "text-white",
        badge: "bg-white/20 text-white",
        grainient: { c1: "#60A5FA", c2: "#3B82F6", c3: "#93C5FD" } // Blue-400, Blue-500, Blue-300
    },
    purple: {
        active: "text-white border-purple-500/50 shadow-lg shadow-purple-500/20",
        inactive: "bg-purple-50/50 text-slate-600 border-purple-200 hover:bg-purple-100 hover:border-purple-300 hover:text-purple-700 dark:bg-purple-900/10 dark:text-slate-400 dark:border-purple-800/50 dark:hover:bg-purple-900/20 dark:hover:text-purple-400",
        iconActive: "text-white",
        badge: "bg-white/20 text-white",
        grainient: { c1: "#A78BFA", c2: "#8B5CF6", c3: "#C4B5FD" } // Violet-400, Violet-500, Violet-300
    },
    emerald: {
        active: "text-white border-emerald-500/50 shadow-lg shadow-emerald-500/20",
        inactive: "bg-emerald-50/50 text-slate-600 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 hover:text-emerald-700 dark:bg-emerald-900/10 dark:text-slate-400 dark:border-emerald-800/50 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400",
        iconActive: "text-white",
        badge: "bg-white/20 text-white",
        grainient: { c1: "#34D399", c2: "#10B981", c3: "#6EE7B7" } // Emerald-400, Emerald-500, Emerald-300
    },
    orange: {
        active: "text-white border-orange-500/50 shadow-lg shadow-orange-500/20",
        inactive: "bg-orange-50/50 text-slate-600 border-orange-200 hover:bg-orange-100 hover:border-orange-300 hover:text-orange-700 dark:bg-orange-900/10 dark:text-slate-400 dark:border-orange-800/50 dark:hover:bg-orange-900/20 dark:hover:text-orange-400",
        iconActive: "text-white",
        badge: "bg-white/20 text-white",
        grainient: { c1: "#FBBF24", c2: "#F59E0B", c3: "#FCD34D" } // Amber-400, Amber-500, Amber-300
    }
};

const SidebarItem = ({ item, collapsed, onClick, theme = 'blue' }) => {
    const Icon = item.icon;
    const currentTheme = themeStyles[theme] || themeStyles.blue;
    const colors = currentTheme.grainient;

    const content = (isActive) => (
        <div
            className={cn(
                "relative flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 group overflow-hidden border",
                isActive ? currentTheme.active : currentTheme.inactive,
                collapsed ? "justify-center px-2" : ""
            )}
        >
            {/* Grainient Background when Active - Fixed size to prevent resize flickering */}
            {isActive && (
                <div className="absolute inset-0 z-0 overflow-hidden">
                    <div className="absolute inset-0" style={{ minWidth: '280px', width: '280px' }}>
                        <Grainient
                            color1={colors.c1}
                            color2={colors.c2}
                            color3={colors.c3}
                            timeSpeed={0.2}
                            colorBalance={0.5}
                            grainAmount={0.05}
                            zoom={1.2}
                            uSaturation={1.2}
                        />
                    </div>
                </div>
            )}

            {/* Icon */}
            <div className="relative z-10 flex items-center justify-center flex-shrink-0">
                <Icon
                    className={cn(
                        "w-5 h-5 transition-transform duration-300 group-hover:scale-110",
                        isActive ? currentTheme.iconActive : "text-current"
                    )}
                    strokeWidth={isActive ? 2.5 : 2}
                />
            </div>

            {/* Label & Badge (Only visible when expanded) */}
            <AnimatePresence mode='wait'>
                {!collapsed && (
                    <motion.div
                        initial={{ opacity: 0, width: 0 }}
                        animate={{
                            opacity: 1,
                            width: 'auto',
                            transition: {
                                opacity: { delay: 0.08, duration: 0.2 },
                                width: { type: "spring", stiffness: 260, damping: 26, restDelta: 0.01 }
                            }
                        }}
                        exit={{
                            opacity: 0,
                            width: 0,
                            transition: {
                                opacity: { duration: 0.12 },
                                width: { type: "spring", stiffness: 300, damping: 30 }
                            }
                        }}
                        className="flex-1 flex items-center justify-between overflow-hidden whitespace-nowrap ml-3 relative z-10"
                    >
                        <span className={cn(
                            "text-sm font-medium tracking-wide truncate",
                            isActive ? "font-bold" : ""
                        )}>
                            {item.title}
                        </span>

                        {item.badge && (
                            <div className={cn(
                                "h-5 min-w-5 px-1.5 rounded-md flex items-center justify-center text-[10px] font-bold ml-2",
                                isActive ? currentTheme.badge : "bg-red-500 text-white shadow-sm shadow-red-500/30"
                            )}>
                                {item.badge}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    const link = (
        <NavLink
            to={item.path}
            onClick={onClick}
            className={({ isActive }) => cn(
                "block outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl mb-2 group",
                isActive ? "active-nav-item" : ""
            )}
        >
            {({ isActive }) => content(isActive)}
        </NavLink>
    );

    if (collapsed) {
        return (
            <Tooltip
                content={item.title}
                placement="right"
                delay={0}
                closeDelay={0}
                classNames={{
                    content: "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 font-medium px-3 py-1.5 rounded-lg text-xs"
                }}
            >
                <div className="w-full">
                    {link}
                </div>
            </Tooltip>
        );
    }

    return link;
};

export default SidebarItem;
